import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

import Iconify from 'src/components/iconify';

export default function InkfrogImportSummary({ data, hasConnectedEbay }) {
  const {
    totalRows,
    auctionCount,
    fixedCount,
    zeroQtyCount,
    withImagesCount,
    emptyTitleCount,
    withInventoryCount,
    ebayUsers,
    ebayMarkets,
    fileName,
    fileSize,
  } = data;

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon="eva:file-text-fill" width={20} sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle2">{fileName}</Typography>
        <Typography variant="caption" color="text.secondary">
          ({(fileSize / 1024).toFixed(0)} KB)
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 1.5,
        }}
      >
        <StatItem label="Total Listings" value={totalRows} icon="eva:layers-fill" />
        <StatItem label="In Stock" value={withInventoryCount} icon="eva:shopping-bag-fill" color="success.main" />
        <StatItem label="Auctions" value={auctionCount} icon="eva:pricetags-fill" />
        <StatItem label="Fixed Price" value={fixedCount} icon="eva:credit-card-fill" />
        <StatItem label="Out of Stock" value={zeroQtyCount} icon="eva:archive-fill" color="warning.main" />
        <StatItem label="With Photos" value={withImagesCount} icon="eva:image-fill" />
      </Box>

      {ebayUsers.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            eBay Account{ebayUsers.length > 1 ? 's' : ''}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {ebayUsers.map((user) => (
              <Chip key={user} label={user} size="small" variant="outlined" />
            ))}
          </Stack>
        </Box>
      )}

      {ebayMarkets.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            eBay Market{ebayMarkets.length > 1 ? 's' : ''}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {ebayMarkets.map((market) => (
              <Chip key={market} label={market} size="small" variant="soft" />
            ))}
          </Stack>
        </Box>
      )}

      <Alert severity="info" variant="outlined">
        <Typography variant="body2">
          <strong>How it works:</strong> We look up your live eBay listings and match them to
          this file by SKU. Matched listings come in with their eBay details, and the rest come in
          as drafts. Photos are copied over too.
        </Typography>
      </Alert>

      {hasConnectedEbay === false && (
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">
            <strong>Tip:</strong> Connect your eBay account on the Marketplaces page first.
            Without it, every listing comes in as a draft.
          </Typography>
        </Alert>
      )}

      {emptyTitleCount > 0 && (
        <Alert severity="warning" variant="outlined">
          {emptyTitleCount} row{emptyTitleCount > 1 ? 's' : ''} with empty titles will be skipped.
        </Alert>
      )}
    </Stack>
  );
}

InkfrogImportSummary.propTypes = {
  data: PropTypes.shape({
    totalRows: PropTypes.number,
    auctionCount: PropTypes.number,
    fixedCount: PropTypes.number,
    zeroQtyCount: PropTypes.number,
    withImagesCount: PropTypes.number,
    emptyTitleCount: PropTypes.number,
    withInventoryCount: PropTypes.number,
    ebayUsers: PropTypes.arrayOf(PropTypes.string),
    ebayMarkets: PropTypes.arrayOf(PropTypes.string),
    fileName: PropTypes.string,
    fileSize: PropTypes.number,
  }),
  hasConnectedEbay: PropTypes.bool,
};

function StatItem({ label, value, icon, color = 'text.primary' }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'background.neutral',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Iconify icon={icon} width={20} sx={{ color: 'text.secondary' }} />
      <Box>
        <Typography variant="subtitle2" sx={{ color }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Box>
    </Box>
  );
}

StatItem.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  icon: PropTypes.string,
  color: PropTypes.string,
};
